import React, { useMemo, useState, useEffect } from "react";
import { isToday } from "date-fns";
import RGL, { WidthProvider } from "react-grid-layout";
const ReactGridLayout = WidthProvider(RGL);
import RGLStyle from "react-grid-layout/css/styles.css";
import ResizeableStyle from "react-resizable/css/styles.css";
import { Maybe } from "true-myth";

import styled from "styles/styled_components";

import useSelfRoute from "helpers/hooks/useSelfRoute";
import {
  getGridDimensions,
  makeHours,
  makeGridLines,
  makePeopleAndBlocks,
  makeTimeMarker,
  ROW_HEIGHT,
  makeDayParts,
} from "helpers/schedule/day_view";
import { ILocation, IDayPart } from "types/account";
import { ISchedule } from "types/schedules";

const StyledWrapper = styled.div`
  overflow: hidden;
`;

interface IScheduleDayView {
  day: Date;
  dayParts: IDayPart[];
  schedule: ISchedule;
  storeLocation: Maybe<ILocation>;
}

const ONE_MINUTE = 1 * 60 * 1000;

export default function ScheduleDayView({
  day,
  dayParts,
  schedule,
  storeLocation,
}: IScheduleDayView) {
  // Notify iframe parent of self route
  useSelfRoute("/workforce/schedule_day");

  const gridDimensions = useMemo(() => {
    const tradingDayEnd = storeLocation
      .andThen((location) => Maybe.of(location.tradingDayCloseHour))
      .unwrapOr(2); // 2am

    return getGridDimensions(day, tradingDayEnd, tradingDayEnd);
  }, [day, storeLocation]);

  const [gridLines, gridLinesLayout] = useMemo(
    () => makeGridLines(gridDimensions),
    [day, gridDimensions]
  );
  const [hourLabels, hourLayout] = useMemo(
    () => makeHours(day, gridDimensions),
    [day, gridDimensions]
  );
  const [dayPartEls, dayPartsLayout] = useMemo(
    () => makeDayParts(dayParts, gridDimensions),
    [dayParts, gridDimensions]
  );

  const [peopleAndBlocks, peopleShiftLayout] = useMemo(
    () => makePeopleAndBlocks(schedule, day, gridDimensions),
    [schedule, day, gridDimensions]
  );

  // Display a current time marker when viewing today
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  useEffect(() => {
    // Every minute, update current time to rerender
    let intervalId: number;
    if (isToday(day)) {
      intervalId = window.setInterval(() => {
        setCurrentTime(new Date());
      }, ONE_MINUTE);
    }
    return () => {
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, []);

  const [timeMarker, timeMarkerLayout] = useMemo(
    () => makeTimeMarker(day, currentTime, gridDimensions),
    [currentTime]
  );

  return (
    <>
      <link rel="stylesheet" href={RGLStyle} />
      <link rel="stylesheet" href={ResizeableStyle} />
      <StyledWrapper>
        <ReactGridLayout
          cols={gridDimensions.totalCols}
          containerPadding={[10, 10]}
          isDraggable={false}
          isResizable={false}
          layout={[
            ...gridLinesLayout,
            ...hourLayout,
            ...dayPartsLayout,
            ...peopleShiftLayout,
            ...timeMarkerLayout,
          ]}
          rowHeight={ROW_HEIGHT}
          margin={[0, 0]}
        >
          {gridLines}
          {hourLabels}
          {dayPartEls}
          {peopleAndBlocks}
          {timeMarker}
        </ReactGridLayout>
      </StyledWrapper>
    </>
  );
}
